import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { withStyles } from '@material-ui/core/styles';
import { doFetch } from '@trdk/style/Redux/fetch.actions';
import {
  Grid, Paper, Divider, Button, CircularProgress,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import * as Yup from 'yup';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import {
  getElementBySysId, setDateTimeToMidnight, getLicenseListFromLicenseSysIDs,
  getSysIdListFromObjectList, isElementInList, getLicenseHeader, goBack,
} from '../utils';
import { fetchAssignments } from '../services/fetch/load-form-service';
import TitleField from '../components/title-field';
import { createOrUpdateWorkPlan } from '../services/submit/submit-servce';
import NotificationBox from '../components/notification-box';

const validationSchema = Yup.object().shape({
  date: Yup.date().required('Feltet er påkrevd'),
  controllers: Yup.array().required('Må ha minst én kontrollør'),
  licenses: Yup.array().required('Må ha minst én bevilling'),
});

class EditWorkPlan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      submitted: false,
      submitError: false,
    };
  }

  render() {
    const { submitting, submitted, submitError } = this.state;

    const {
      dispatch, classes, history,
      match: { params: { assignmentID, workPlanID } },
      assignments: { payload: assignmentsPayload },
      licenses: { payload: licensesPayload },
      controllers: { payload: controllersPayload },
    } = this.props;

    const assignment = getElementBySysId(assignmentsPayload, assignmentID);
    if (assignment == null) {
      return (
        <div>
          {`Ingen kontrolloppdrag funnet med sysId: ${assignmentID}`}
        </div>
      );
    }

    const workPlan = getElementBySysId(assignment.workPlans, workPlanID);
    if (workPlan == null) {
      return (
        <div>
          {`Ingen arbeidsplan funnet med sysId: ${workPlanID}`}
        </div>
      );
    }

    if (submitting && !submitted) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    const controlListLicenseSysIDs = (assignment.controlList != null)
      ? assignment.controlList.licenses : null;

    const controlListLicenses = getLicenseListFromLicenseSysIDs(
      licensesPayload, controlListLicenseSysIDs,
    );

    const licenseObjectsInWorkPlan = getLicenseListFromLicenseSysIDs(
      licensesPayload, workPlan.licensesSysId,
    );

    const defaultControllers = [];
    if (workPlan.controllers) {
      controllersPayload.forEach((c) => {
        if (isElementInList(workPlan.controllers, c.controllerSysId, 'controllerSysId')) {
          defaultControllers.push(c);
        }
      });
    }

    return (
      <div>
        <TitleField title="Rediger arbeidsplan" subtitle={assignment.controlList.name} />
        <Formik
          initialValues={{
            date: workPlan.date,
            controllers: defaultControllers,
            licenses: licenseObjectsInWorkPlan,
          }}
          onSubmit={(v) => {
            this.setState({ submitted: false });
            this.setState({ submitting: true });
            const payload = {
              sysId: workPlanID,
              date: v.date,
              assignment: assignmentID,
              controllers: getSysIdListFromObjectList(v.controllers, 'controllerSysId'),
              licenses: getSysIdListFromObjectList(v.licenses, 'sysId'),
            };
            createOrUpdateWorkPlan(payload, 'PUT').then((res) => {
              this.setState({ submitting: false });
              this.setState({ submitted: true });
              if (!res.ok) {
                this.setState({ submitError: true });
                throw res;
              }

              dispatch(doFetch('ASSIGNMENTS', fetchAssignments().then((assignmentsRes) => {
                if (!assignmentsRes.ok) { throw assignmentsRes; }
                goBack(history);
                return assignmentsRes.json();
              })));
            });
          }}
          validationSchema={validationSchema}
        >
          {({
            values, errors, touched, handleBlur, setFieldValue,
          }) => (
            <Form>
              <Paper className={classes.paper} elevation={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                      <KeyboardDatePicker
                        name="date"
                        error={!!(errors.date && touched.date)}
                        label="Dato"
                        disablePast
                        cancelLabel="Avbryt"
                        value={values.date}
                        format="dd.MM.yyyy"
                        invalidDateMessage="Ugyldig dato"
                        minDateMessage="Dato kan ikke være før dagens dato"
                        onBlur={(e) => {
                          handleBlur(e);
                        }}
                        onChange={(value) => {
                          setFieldValue('date', setDateTimeToMidnight(value));
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Kontrollører</InputLabel>
                      <Select
                        error={!!(errors.controllers && touched.controllers)}
                        multiple
                        value={values.controllers}
                        onChange={(e) => {
                          setFieldValue('controllers', e.target.value);
                        }}
                        input={<Input />}
                        renderValue={selected => (
                          <div className={classes.chips}>
                            {selected.map(value => (
                              <Chip
                                key={value.controllerSysId}
                                label={`${value.firstName} ${value.lastName} (${value.controllerNumber})`}
                                className={classes.chip}
                              />
                            ))}
                          </div>
                        )}
                      >
                        {controllersPayload.map(controller => (
                          <MenuItem
                            key={controller.controllerSysId}
                            value={controller}
                          >
                            <Checkbox
                              color="primary"
                              checked={isElementInList(
                                values.controllers, controller.controllerSysId, 'controllerSysId',
                              )}
                            />
                            <ListItemText
                              primary={`${controller.firstName} ${controller.lastName} (${controller.controllerNumber})`}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl className={classes.formControl}>
                      <InputLabel>Bevillinger</InputLabel>
                      <Select
                        error={!!(errors.licenses && touched.licenses)}
                        multiple
                        value={values.licenses}
                        onChange={(e) => {
                          setFieldValue('licenses', e.target.value);
                        }}
                        input={<Input />}
                        renderValue={selected => (
                          <div className={classes.chips}>
                            {selected.map(value => (
                              <Chip
                                key={value.sysId}
                                label={getLicenseHeader(value, false)}
                                className={classes.chip}
                              />
                            ))}
                          </div>
                        )}
                      >
                        { /* TODO: don't show licenses where a control has been
                        completed for this control list */ }
                        {controlListLicenses.map(license => (
                          <MenuItem
                            key={license.sysId}
                            value={license}
                          >
                            <Checkbox
                              color="primary"
                              checked={
                                isElementInList(values.licenses, license.sysId, 'sysId')
                              }
                            />
                            <ListItemText primary={getLicenseHeader(license, true)} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={12}>
                    <Button color="primary" type="submit" variant="contained" fullWidth>
                      Lagre
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Form>
          )}
        </Formik>
        {submitted && submitError && (
          <NotificationBox variant="error" header="Feil oppsto under lagring av endringer" showIcon margin="2rem 0" />
        )}
        {submitted && !submitError && (
          <NotificationBox variant="success" header="Endringer lagret" showIcon margin="2rem 0" />
        )}
      </div>
    );
  }
}

EditWorkPlan.propTypes = {
  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  assignments: PropTypes.object.isRequired,
  licenses: PropTypes.object.isRequired,
  controllers: PropTypes.object.isRequired,
};

const styles = theme => ({
  paper: {
    padding: '0.8rem',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '10rem',
    maxWidth: '100%',
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  loading: {
    margin: theme.spacing(5),
    textAlign: 'center',
  },
});

const mapStateToProps = ({
  assignments, controllers, licenses,
}) => ({ assignments, controllers, licenses });

export default connect(mapStateToProps)(withRouter(withStyles(styles)(EditWorkPlan)));
